// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The playfield on a screen, at a scale an author can trust (M9-01, 11 §3.1).
//
// 11 §3.1 asks for "a top-down playfield canvas **at true scale**", and true scale is the part that
// needs stating rather than assuming, because a browser cannot measure a monitor. What it has is the
// CSS reference pixel — 1/96 inch by definition, at a nominal arm's-length viewing distance — and
// what that produces is *nominally* true scale: right on a display whose reported density matches
// its physical one, and out by whatever the display lies by otherwise. Common laptop panels are out
// by 10–20%.
//
// So this offers both, and names them apart. **Fit** is the working view and the default: the whole
// playfield, as large as the canvas allows. **True scale** is a mode an author enters deliberately
// to judge a real distance — is that gap wider than a ball, does that shot look reachable — carrying
// a calibration factor the author sets once by matching an on-screen ruler against a real one. A
// canvas that silently claimed true scale and was 15% out would be worse than one that never
// claimed it: every judgement made in it would be wrong in the same direction, and nothing on screen
// would say so.
//
// **PH-001's frame throughout**: metres, origin at the centre of the playfield, +x to the player's
// right, +y up the playfield away from them. Screen y grows downward, so exactly one place in this
// file flips it — and it is the same place in both directions, which is what stops a canvas that
// draws correctly and hit-tests upside down.

import type { Point2 } from '../../core/ts/machine'

/** CSS pixels per inch, by definition. Not a measurement — see the note above. */
export const CSS_PIXELS_PER_INCH = 96

export const METRES_PER_INCH = 0.0254

/** The 1 mm design grid, in metres (11 §3.1). */
export const GRID_METRES = 0.001

/**
 * How far in and out the view goes, as pixels per metre.
 *
 * The floor shows a 1.07 m playfield in about 200 px — small enough to see the whole machine on a
 * laptop with an inspector open beside it. The ceiling is about seven times true scale, which puts
 * the 1 mm grid at roughly 2.6 px: the finest zoom at which the grid is still a grid rather than a
 * grey wash, and far past what placing a device needs.
 */
export const MIN_PIXELS_PER_METRE = 190

export const MAX_PIXELS_PER_METRE = 26_000

/**
 * The width of the playfield's margin in the fitted view, as a fraction of the canvas.
 *
 * Devices sit right up against the side rails, and a fit with no margin would put a slingshot's
 * handle half off the canvas where it cannot be grabbed.
 */
export const FIT_MARGIN = 0.04

/** The playfield's own size, metres. */
export interface PlayfieldSize {
  readonly width: number
  readonly length: number
}

/**
 * What part of the playfield is on screen, and how large.
 *
 * `centre` is the playfield point under the middle of the canvas, which is the form that makes
 * zooming about a pointer one subtraction rather than a special case: the pointer's playfield
 * position is held fixed and the centre moves to suit.
 */
export interface Viewport {
  /** Canvas size in CSS pixels. */
  readonly width: number
  readonly height: number
  readonly pixelsPerMetre: number
  readonly centre: Point2
}

/**
 * Pixels per metre at true scale.
 *
 * `calibration` is the author's own correction: the ratio between the length a ruler on screen
 * *claims* and the length it *measures*. 1 for an uncalibrated display, which is the CSS reference
 * and the best guess available without asking.
 */
export function trueScalePixelsPerMetre(calibration = 1): number {
  return (CSS_PIXELS_PER_INCH / METRES_PER_INCH) * calibration
}

/** Is this view at true scale, within the tolerance of a pixel per metre? */
export function isTrueScale(view: Viewport, calibration = 1): boolean {
  return Math.abs(view.pixelsPerMetre - trueScalePixelsPerMetre(calibration)) < 1
}

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value
}

/**
 * The whole playfield, as large as it goes.
 *
 * The limiting dimension decides, so a wide-body in a tall canvas is bounded by its width and the
 * same machine in a short one by its length. A canvas with no area yet — the frame before layout
 * has run — answers the floor rather than an infinity, so the first paint is merely small instead of
 * a division by zero drawn as a blank page.
 */
export function fitViewport(playfield: PlayfieldSize, width: number, height: number): Viewport {
  const usableWidth = width * (1 - FIT_MARGIN * 2)
  const usableHeight = height * (1 - FIT_MARGIN * 2)
  const byWidth = playfield.width > 0 ? usableWidth / playfield.width : 0
  const byLength = playfield.length > 0 ? usableHeight / playfield.length : 0
  const both = Math.min(byWidth, byLength)
  return {
    width,
    height,
    pixelsPerMetre: clamp(both > 0 ? both : MIN_PIXELS_PER_METRE, MIN_PIXELS_PER_METRE, MAX_PIXELS_PER_METRE),
    centre: [0, 0],
  }
}

/** Where a playfield point lands on the canvas, in CSS pixels from its top-left corner. */
export function toScreen(view: Viewport, point: Point2): readonly [number, number] {
  return [
    view.width / 2 + (point[0] - view.centre[0]) * view.pixelsPerMetre,
    // The one flip. +y is up the playfield and down the screen is positive.
    view.height / 2 - (point[1] - view.centre[1]) * view.pixelsPerMetre,
  ]
}

/** …and the way back, which is the same arithmetic read backwards. */
export function toPlayfield(view: Viewport, x: number, y: number): Point2 {
  return [
    view.centre[0] + (x - view.width / 2) / view.pixelsPerMetre,
    view.centre[1] - (y - view.height / 2) / view.pixelsPerMetre,
  ]
}

/**
 * Zoom, holding one screen point still.
 *
 * **The point under the pointer does not move**, which is the whole of what makes wheel-zoom feel
 * like a magnifying glass rather than a slider. Zooming about the canvas centre instead is the
 * common mistake and it is immediately recognisable: the thing you were looking at slides away as
 * you zoom in on it.
 */
export function zoomedAt(view: Viewport, factor: number, x: number, y: number): Viewport {
  const held = toPlayfield(view, x, y)
  const scaled = clamp(view.pixelsPerMetre * factor, MIN_PIXELS_PER_METRE, MAX_PIXELS_PER_METRE)
  if (scaled === view.pixelsPerMetre) return view
  const next: Viewport = { ...view, pixelsPerMetre: scaled }
  // Where that point would land now, and how far the centre has to move to put it back.
  const after = toPlayfield(next, x, y)
  return { ...next, centre: [next.centre[0] + held[0] - after[0], next.centre[1] + held[1] - after[1]] }
}

/** Zoom to an exact scale about the canvas centre. What the fit and true-scale controls do. */
export function atScale(view: Viewport, pixelsPerMetre: number): Viewport {
  return {
    ...view,
    pixelsPerMetre: clamp(pixelsPerMetre, MIN_PIXELS_PER_METRE, MAX_PIXELS_PER_METRE),
  }
}

/** Drag the view by a screen distance. */
export function pannedBy(view: Viewport, dx: number, dy: number): Viewport {
  return {
    ...view,
    centre: [
      view.centre[0] - dx / view.pixelsPerMetre,
      view.centre[1] + dy / view.pixelsPerMetre,
    ],
  }
}

/**
 * Keep the playfield in sight.
 *
 * **A bound on the centre, not on the edges**, and the difference matters when the playfield is
 * smaller than the canvas: bounding the edges would fight the fitted view, which deliberately shows
 * margin on all four sides. This says only that the point under the middle of the canvas stays
 * within the playfield, which lets an author push the machine to any edge to work at it and never
 * lets them lose it off-screen entirely — the state whose only remedy is a control they have to
 * find.
 */
export function clampedTo(view: Viewport, playfield: PlayfieldSize): Viewport {
  const x = clamp(view.centre[0], -playfield.width / 2, playfield.width / 2)
  const y = clamp(view.centre[1], -playfield.length / 2, playfield.length / 2)
  if (x === view.centre[0] && y === view.centre[1]) return view
  return { ...view, centre: [x, y] }
}

/** A canvas that changed size, keeping the scale and what is under the middle of it. */
export function resized(view: Viewport, width: number, height: number): Viewport {
  return { ...view, width, height }
}

/**
 * How far a screen tolerance is in metres, at this scale.
 *
 * **Snapping tolerances are decided in pixels and applied in metres.** A tolerance fixed in metres
 * would be unusable at both ends: at the fitted view a 2 mm catch radius is a fifth of a pixel and
 * nothing ever snaps, and at seven times true scale it is 130 px and everything snaps to the same
 * place. In pixels it is the same fingertip at every zoom.
 */
export function metresPerPixel(view: Viewport, pixels: number): number {
  return pixels / view.pixelsPerMetre
}

/**
 * The grid spacing to actually draw, metres.
 *
 * The design grid is 1 mm and always is — that is what a device snaps to. What is *drawn* steps up
 * through 1, 5, 10, 50, 100 mm as the view zooms out, because a grid whose lines are half a pixel
 * apart is not a grid, it is a flat grey field that hides the machine underneath it.
 */
export function drawnGridMetres(view: Viewport, minimumPixels = 8): number {
  const steps = [0.001, 0.005, 0.01, 0.05, 0.1] as const
  for (const step of steps) {
    if (step * view.pixelsPerMetre >= minimumPixels) return step
  }
  // Zoomed further out than the coarsest step: half a metre of playfield is about a fifth of it,
  // which is a grid of five lines. Coarser than that stops being a grid and starts being a border.
  return 0.5
}
